/**
 * A pool of DOM nodes.
 *
 * Effects create and destroy nodes constantly by nature — a burst here, a
 * pulse there — and `createElement` + `appendChild` + `remove` per effect is
 * both allocation churn and a style/layout invalidation each way. Pooled nodes
 * are created once, parked with `visibility: hidden` (which costs no layout)
 * and handed back out.
 *
 * Two ways to run out, because there are two kinds of caller:
 *
 *  - `acquire()` grows rather than starving, and warns once when it does. For
 *    nodes whose absence would be a bug — a missing tile is not a missing
 *    sparkle — outgrowing the configured size means the balance number is
 *    wrong, and hearing about that beats silently paying for it.
 *  - `tryAcquire()` returns null once `maxSize` is reached. For effects, where
 *    the budget is the point: allocating during a burst costs a frame at the
 *    exact moment the frame is busiest, and the thirty-third simultaneous
 *    sparkle is not visible anyway.
 */
export class NodePool<T extends HTMLElement> {
  private readonly free: T[] = [];
  private readonly busy = new Set<T>();
  private grown = 0;
  private refused = 0;

  constructor(
    private readonly factory: () => T,
    private readonly parent: HTMLElement,
    private readonly initialSize: number,
    private readonly label = 'pool',
    /** Hard ceiling for `tryAcquire`. `acquire` ignores it and grows. */
    private readonly maxSize = Number.POSITIVE_INFINITY,
  ) {
    for (let i = 0; i < initialSize; i++) {
      this.free.push(this.create());
    }
  }

  private create(): T {
    const node = this.factory();
    this.parent.appendChild(node);
    return node;
  }

  acquire(): T {
    let node = this.free.pop();

    if (!node) {
      this.grown += 1;
      if (this.grown === 1) {
        console.warn(
          `[${this.label}] pool of ${this.initialSize} exhausted; growing. ` +
            `Raise BALANCE.perf.*PoolSize instead of paying for this at runtime.`,
        );
      }
      node = this.create();
    }

    this.busy.add(node);
    return node;
  }

  /**
   * Null when the budget is spent. Callers that can drop their effect use this
   * instead of `acquire`, so a burst costs nothing rather than growing the
   * pool mid-frame.
   */
  tryAcquire(): T | null {
    const node = this.free.pop();
    if (node) {
      this.busy.add(node);
      return node;
    }

    if (this.busy.size >= this.maxSize) {
      this.refused += 1;
      return null;
    }

    return this.acquire();
  }

  release(node: T): void {
    if (!this.busy.delete(node)) return;
    this.free.push(node);
  }

  releaseAll(): void {
    for (const node of this.busy) this.free.push(node);
    this.busy.clear();
  }

  get stats(): { free: number; busy: number; total: number; grown: number; refused: number } {
    return {
      free: this.free.length,
      busy: this.busy.size,
      total: this.free.length + this.busy.size,
      grown: this.grown,
      // Non-zero means the budget is actually biting, which is the measurement
      // that would justify adaptive degradation. Until it does, there is
      // nothing to adapt to.
      refused: this.refused,
    };
  }

  destroy(): void {
    for (const node of [...this.free, ...this.busy]) node.remove();
    this.free.length = 0;
    this.busy.clear();
  }
}
